import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  ExternalLink, 
  Search,
  Users,
  Clock
} from 'lucide-react';

const CaseStudies: React.FC = () => {
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const filters = [
    { id: 'all', label: 'All Projects' },
    { id: 'ecommerce', label: 'E-commerce' },
    { id: 'healthcare', label: 'Healthcare' },
    { id: 'fintech', label: 'Fintech' },
    { id: 'education', label: 'Education' },
    { id: 'travel', label: 'Travel & Hospitality' }
  ];

  const caseStudies = [
    {
      id: '1',
      title: 'US Fashion Resale Platform Scales to 100K Monthly Transactions',
      category: 'ecommerce',
      industry: 'E-commerce & Retail',
      description: 'Complete redesign and development of a multi-vendor fashion resale platform with advanced search, AI recommendations, and seamless payment processing.',
      image: '/images/case-study-fashion.jpg',
      technologies: ['React', 'Node.js', 'PostgreSQL', 'Stripe', 'Elasticsearch', 'Redis'],
      results: [
        { metric: '150%', label: 'Increase in Sales' },
        { metric: '100K+', label: 'Monthly Transactions' },
        { metric: '40%', label: 'Reduced Load Time' },
        { metric: '95%', label: 'User Satisfaction' }
      ],
      duration: '6 months',
      team: '8 developers',
      challenge: 'The existing platform struggled with scalability, poor user experience, and limited vendor management capabilities.',
      solution: 'We built a modern, scalable platform with microservices architecture, advanced search functionality, and comprehensive vendor dashboard.',
      impact: 'The new platform enabled the client to scale from 10K to 100K+ monthly transactions while maintaining excellent performance and user experience.'
    },
    {
      id: '2',
      title: 'Healthcare AI Platform Reconciles $300M+ in OTA Commissions Automatically',
      category: 'healthcare',
      industry: 'Healthcare & Pharmaceuticals',
      description: 'AI-powered healthcare platform that automates commission reconciliation for Online Travel Agencies (OTAs) with real-time processing and fraud detection.',
      image: '/images/case-study-healthcare.jpg',
      technologies: ['Python', 'TensorFlow', 'PostgreSQL', 'Docker', 'Kubernetes', 'AWS'],
      results: [
        { metric: '$300M+', label: 'Commission Processed' },
        { metric: '99.9%', label: 'Accuracy Rate' },
        { metric: '80%', label: 'Time Reduction' },
        { metric: 'Zero', label: 'Manual Errors' }
      ],
      duration: '8 months',
      team: '12 developers',
      challenge: 'Manual commission reconciliation was time-consuming, error-prone, and couldn\'t scale with growing transaction volumes.',
      solution: 'We developed an AI-powered platform with machine learning algorithms for pattern recognition, automated reconciliation, and real-time fraud detection.',
      impact: 'The platform now processes over $300M in commissions automatically with 99.9% accuracy, saving thousands of hours of manual work.'
    },
    {
      id: '3',
      title: 'US Fintech\'s AI Financial Modeling Secures $2M+ Funding',
      category: 'fintech',
      industry: 'Banking & Fintech',
      description: 'Advanced AI financial modeling platform that provides real-time market analysis, risk assessment, and investment recommendations for financial institutions.',
      image: '/images/case-study-fintech.jpg',
      technologies: ['Python', 'Machine Learning', 'React', 'Node.js', 'MongoDB', 'TensorFlow'],
      results: [
        { metric: '$2M+', label: 'Funding Secured' },
        { metric: '25%', label: 'Better Predictions' },
        { metric: '50+', label: 'Financial Models' },
        { metric: '24/7', label: 'Real-time Analysis' }
      ],
      duration: '10 months',
      team: '15 developers',
      challenge: 'Traditional financial modeling was slow, inaccurate, and couldn\'t adapt to rapidly changing market conditions.',
      solution: 'We created an AI-powered platform with advanced machine learning models, real-time data processing, and comprehensive risk assessment capabilities.',
      impact: 'The platform\'s superior accuracy and speed helped the client secure $2M+ in funding and attracted major financial institutions as clients.'
    },
    {
      id: '4',
      title: 'Pakistan Furniture Leader\'s Shopify Migration Drives 55% Growth',
      category: 'ecommerce',
      industry: 'E-commerce & Retail',
      description: 'Complete migration from legacy e-commerce platform to Shopify Plus with custom development, inventory management, and multi-channel integration.',
      image: '/images/case-study-furniture.jpg',
      technologies: ['Shopify Plus', 'Liquid', 'React', 'Node.js', 'PostgreSQL', 'Stripe'],
      results: [
        { metric: '55%', label: 'Revenue Growth' },
        { metric: '70%', label: 'Faster Checkout' },
        { metric: '3x', label: 'Mobile Traffic' },
        { metric: '90%', label: 'Customer Satisfaction' }
      ],
      duration: '4 months',
      team: '6 developers',
      challenge: 'The existing platform was slow, had poor mobile experience, and limited integration capabilities with modern payment and shipping systems.',
      solution: 'We migrated to Shopify Plus with custom theme development, advanced inventory management, and seamless integration with local payment gateways.',
      impact: 'The new platform resulted in 55% revenue growth, significantly improved user experience, and better operational efficiency.'
    },
    {
      id: '5',
      title: 'Virtual Learning Platform Transforms Education for 50K+ Students',
      category: 'education',
      industry: 'Education & EdTech',
      description: 'Comprehensive virtual learning platform with interactive classrooms, AI-powered tutoring, and comprehensive learning analytics.',
      image: '/images/case-study-education.jpg',
      technologies: ['React', 'WebRTC', 'Node.js', 'MongoDB', 'AI/ML', 'AWS'],
      results: [
        { metric: '50K+', label: 'Active Students' },
        { metric: '85%', label: 'Engagement Rate' },
        { metric: '40%', label: 'Learning Improvement' },
        { metric: '99.9%', label: 'Uptime' }
      ],
      duration: '12 months',
      team: '20 developers',
      challenge: 'Traditional online learning platforms lacked interactivity, personalization, and comprehensive analytics for student progress tracking.',
      solution: 'We built a next-generation platform with real-time video conferencing, AI-powered personalized learning paths, and detailed analytics dashboard.',
      impact: 'The platform now serves 50K+ students with 85% engagement rate and 40% improvement in learning outcomes compared to traditional methods.'
    },
    {
      id: '6',
      title: 'Hotel Chain Management System Increases Revenue by 35%',
      category: 'travel',
      industry: 'Travel & Hospitality',
      description: 'Integrated hotel management system with booking engine, revenue management, guest services, and comprehensive analytics dashboard.',
      image: '/images/case-study-hotel.jpg',
      technologies: ['React', 'Node.js', 'PostgreSQL', 'Redis', 'Payment APIs', 'Analytics'],
      results: [
        { metric: '35%', label: 'Revenue Increase' },
        { metric: '60%', label: 'Booking Efficiency' },
        { metric: '25%', label: 'Cost Reduction' },
        { metric: '4.8/5', label: 'Guest Rating' }
      ],
      duration: '7 months',
      team: '10 developers',
      challenge: 'Multiple disconnected systems led to operational inefficiencies, poor guest experience, and missed revenue opportunities.',
      solution: 'We developed an integrated platform with unified booking system, dynamic pricing, guest management, and comprehensive analytics.',
      impact: 'The integrated system increased revenue by 35%, improved operational efficiency by 60%, and enhanced guest satisfaction scores.'
    }
  ];

  const filteredCaseStudies = caseStudies.filter(study => {
    const matchesFilter = selectedFilter === 'all' || study.category === selectedFilter;
    const matchesSearch = study.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         study.industry.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-12">
          <div className="text-center">
            <div className="mb-4">
              <span className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium text-blue-100 border border-white/30">
                📊 Success Stories
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 leading-tight">
              Case <span className="text-blue-300 bg-gradient-to-r from-blue-300 to-cyan-300 bg-clip-text text-transparent">Studies</span>
            </h1>
            <p className="text-lg lg:text-xl mb-8 text-blue-100 leading-relaxed max-w-4xl mx-auto">
              Discover how we've helped businesses across industries achieve remarkable results through innovative digital solutions and strategic consulting.
            </p>
            
            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto mt-12">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                <div className="text-2xl font-bold">50+</div>
                <div className="text-blue-200 text-sm">Projects</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                <div className="text-2xl font-bold">98%</div>
                <div className="text-blue-200 text-sm">Success Rate</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                <div className="text-2xl font-bold">$2M+</div>
                <div className="text-blue-200 text-sm">Revenue Generated</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                <div className="text-2xl font-bold">24/7</div>
                <div className="text-blue-200 text-sm">Support</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filters and Search */}
      <section className="py-16 bg-gradient-to-br from-white via-gray-50/50 to-blue-50/30 dark:from-gray-900 dark:via-gray-800/50 dark:to-blue-900/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-8 items-center justify-between">
            {/* Search */}
            <div className="relative w-full lg:w-96">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-indigo-600/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search case studies..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 border border-gray-200/50 dark:border-gray-600/50 rounded-2xl bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-lg hover:shadow-xl transition-all duration-300"
                />
              </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-3">
              {filters.map((filter) => (
                <button
                  key={filter.id}
                  onClick={() => setSelectedFilter(filter.id)}
                  className={`group px-6 py-3 rounded-2xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl ${
                    selectedFilter === filter.id
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white scale-105'
                      : 'bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-800 hover:scale-105 border border-gray-200/50 dark:border-gray-600/50'
                  }`}
                >
                  <span className="group-hover:scale-110 transition-transform duration-300 inline-block">
                    {filter.label}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="py-12 bg-gradient-to-br from-gray-50 via-white to-green-50/30 dark:from-gray-800 dark:via-gray-900 dark:to-green-900/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-200 rounded-full text-sm font-medium mb-6">
              🏆 Success Stories
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
              Our <span className="text-green-600 dark:text-green-400">Case Studies</span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Discover how we've helped businesses achieve remarkable results through innovative digital solutions and strategic implementation.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl mx-auto">
            {filteredCaseStudies.map((study) => (
              <div key={study.id} className="group relative bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm rounded-2xl shadow-md overflow-hidden hover:shadow-lg transition-all duration-300 border border-gray-100/50 dark:border-gray-700/50 hover:border-green-200 dark:hover:border-green-600 hover:-translate-y-1 hover:scale-102 mx-4">
                {/* Header with Achievement */}
                <div className="h-32 bg-gradient-to-br from-green-500 via-emerald-600 to-teal-600 flex items-center justify-center relative overflow-hidden">
                  <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors duration-300"></div>
                  
                  {/* Animated Background Pattern */}
                  <div className="absolute inset-0 opacity-20">
                    <div className="absolute inset-0" style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M20 20c0-11.046-8.954-20-20-20s-20 8.954-20 20 8.954 20 20 20 20-8.954 20-20zm-20 0c0-11.046 8.954-20 20-20s20 8.954 20 20-8.954 20-20 20-20-8.954-20-20z'/%3E%3C/g%3E%3C/svg%3E")`,
                    }}></div>
                  </div>
                  
                  <div className="text-white text-center relative z-10">
                    <div className="text-2xl font-bold mb-1 group-hover:scale-110 transition-transform duration-300">{study.results[0].metric}</div>
                    <div className="text-green-100 text-xs font-medium">{study.results[0].label}</div>
                  </div>
                  
                  {/* Industry Badge */}
                  <div className="absolute top-3 right-3">
                    <span className="px-2 py-1 bg-white/20 backdrop-blur-sm text-white text-xs rounded-full font-medium border border-white/30">
                      {study.industry}
                    </span>
                  </div>
                  
                  {/* Floating Elements */}
                  <div className="absolute top-3 left-3 w-6 h-6 bg-white/20 rounded-full flex items-center justify-center text-white text-xs animate-pulse">
                    ⭐
                  </div>
                  <div className="absolute bottom-3 right-3 w-5 h-5 bg-white/20 rounded-full flex items-center justify-center text-white text-xs animate-bounce">
                    🚀
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3 group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors duration-300 line-clamp-2">
                    {study.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4 text-xs leading-relaxed line-clamp-2 group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors duration-300">
                    {study.description}
                  </p>

                  {/* Results */}
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    {study.results.slice(1, 4).map((result, index) => (
                      <div key={index} className="text-center bg-gray-50 dark:bg-gray-800 rounded-lg p-2 group-hover:bg-gray-100 dark:group-hover:bg-gray-700 transition-colors duration-300">
                        <div className="text-sm font-bold text-green-600 dark:text-green-400 group-hover:scale-110 transition-transform duration-300">
                          {result.metric}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                          {result.label}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Technologies */}
                  <div className="mb-4">
                    <div className="flex flex-wrap gap-1">
                      {study.technologies.slice(0, 3).map((tech, techIndex) => (
                        <span key={techIndex} className="px-2 py-1 bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900/50 dark:to-emerald-900/50 text-green-800 dark:text-green-200 text-xs rounded-full font-medium border border-green-200/50 dark:border-green-700/50 group-hover:scale-105 transition-transform duration-300">
                          {tech}
                        </span>
                      ))}
                      {study.technologies.length > 3 && (
                        <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs rounded-full font-medium">
                          +{study.technologies.length - 3}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Project Details */}
                  <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-4">
                    <div className="flex items-center bg-gray-50 dark:bg-gray-800 px-2 py-1 rounded-lg">
                      <Clock className="h-3 w-3 mr-1 text-green-600 dark:text-green-400" />
                      <span className="font-medium">{study.duration}</span>
                    </div>
                    <div className="flex items-center bg-gray-50 dark:bg-gray-800 px-2 py-1 rounded-lg">
                      <Users className="h-3 w-3 mr-1 text-green-600 dark:text-green-400" />
                      <span className="font-medium">{study.team}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-between">
                    <Link
                      to={`/case-studies/${study.id}`}
                      className="group/btn text-green-600 dark:text-green-400 font-semibold hover:text-green-700 dark:hover:text-green-300 inline-flex items-center text-xs transition-colors duration-300"
                    >
                      <span>Read More</span>
                      <ExternalLink className="ml-1 h-3 w-3 group-hover/btn:translate-x-1 transition-transform duration-300" />
                    </Link>
                    <Link
                      to="/contact"
                      className="group/btn inline-flex items-center px-3 py-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white text-xs font-semibold rounded-lg transition-all duration-300 shadow-md hover:shadow-lg"
                    >
                      <span>Get Solution</span>
                      <ArrowRight className="h-3 w-3 ml-1 group-hover/btn:translate-x-1 transition-transform duration-300" />
                    </Link>
                  </div>
                </div>
                
                {/* Hover Effect Border */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-green-500/20 to-emerald-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10 blur-xl"></div>
              </div>
            ))}
          </div>

          {filteredCaseStudies.length === 0 && (
            <div className="text-center py-16">
              <div className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm rounded-3xl p-12 shadow-lg border border-gray-100/50 dark:border-gray-700/50">
                <div className="text-6xl mb-4">🔍</div>
                <div className="text-gray-500 dark:text-gray-400 text-xl font-medium">
                  No case studies found matching your criteria.
                </div>
                <p className="text-gray-400 dark:text-gray-500 mt-2">
                  Try adjusting your search or filter options.
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-12 bg-gradient-to-br from-blue-600 via-indigo-700 to-purple-800 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}></div>
        </div>
        
        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-white/10 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-32 h-32 bg-white/5 rounded-full blur-2xl animate-bounce"></div>
        <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-white/10 rounded-full blur-xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-8">
            <span className="inline-block px-6 py-3 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium text-blue-100 border border-white/30 mb-6">
              🚀 Ready to Succeed?
            </span>
          </div>
          
          <h2 className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Ready to Create Your <span className="text-blue-300 bg-gradient-to-r from-blue-300 to-cyan-300 bg-clip-text text-transparent">Success Story?</span>
          </h2>
          <p className="text-xl lg:text-2xl text-blue-100 mb-12 max-w-4xl mx-auto leading-relaxed">
            Let's discuss how we can help you achieve similar results for your business and create your own success story with our proven solutions.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-12">
            <Link
              to="/contact"
              className="group inline-flex items-center justify-center px-10 py-5 text-lg font-semibold rounded-2xl bg-white text-blue-600 hover:bg-blue-50 transition-all duration-300 shadow-2xl hover:shadow-3xl hover:scale-105"
            >
              <span>Start Your Project</span>
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
            <Link
              to="/services"
              className="group inline-flex items-center justify-center px-10 py-5 text-lg font-semibold rounded-2xl border-2 border-white text-white hover:bg-white hover:text-blue-600 transition-all duration-300 hover:scale-105"
            >
              <span>Explore Our Services</span>
            </Link>
          </div>
          
          {/* Trust Indicators */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="text-3xl font-bold text-white mb-2">Proven</div>
              <div className="text-blue-200 text-sm">Results</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="text-3xl font-bold text-white mb-2">Expert</div>
              <div className="text-blue-200 text-sm">Team</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="text-3xl font-bold text-white mb-2">24/7</div>
              <div className="text-blue-200 text-sm">Support</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CaseStudies;
